import React, { Component, useState, useEffect } from "react";
import PropTypes from "prop-types";
import Overridable from "react-overridable";
import { Link } from "react-router-dom";
import { Container, Header, Icon, Image } from "semantic-ui-react";
import { Notifications } from "@components/Notifications";
import { Banner } from "@components/Banner";
import { FrontSiteRoutes } from "@routes/urls";
import SignupWithLocalAccount from "./SignupWithLocalAccount";

const CONSTANTS = {
  COLORS: {
    PRIMARY: "#2c5f2d",
  },
  IMAGES: {
    UNIVERSITY_LOGO: "/images/university-logo.png",
    LRC_LOGO: "/images/lrc-logo.png",
  },
  TEXT: {
    SYSTEM_NAME: "Library Information Management System",
    WELCOME_HEADER: "Join Our Community",
    TAGLINE: '"Learn, Reach, Connect"',
    FOOTER_MESSAGE:
      "The RSU Learning Resource Center: A hub of scholarly resources and innovative technologies.",
    LOGIN_PROMPT: "Already have an account?",
    LOGIN_LINK_TEXT: "Sign in here",
    HOME_LINK_TEXT: "Home page",
  },
  ROUTES: {
    LOGIN: "/login",
  },
  BREAKPOINTS: {
    MOBILE: 768,
    SMALL_MOBILE: 480,
    LARGE_MOBILE: 576,
  },
};

const ERROR_PROP_TYPES = {
  hasError: PropTypes.bool,
  errorHeader: PropTypes.string,
  errorMessage: PropTypes.string,
};

const ERROR_DEFAULT_PROPS = {
  hasError: false,
  errorHeader: "",
  errorMessage: "",
};

const COMMON_PROP_TYPES = {
  ...ERROR_PROP_TYPES,
  fetchUserProfile: PropTypes.func.isRequired,
  backgroundImage: PropTypes.string,
  showLogo: PropTypes.bool,
};

const COMMON_DEFAULT_PROPS = {
  ...ERROR_DEFAULT_PROPS,
  backgroundImage: null,
  showLogo: true,
};

// Custom hook for responsive breakpoints
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    isMobile: screenSize.width <= CONSTANTS.BREAKPOINTS.MOBILE,
    isSmallMobile: screenSize.width <= CONSTANTS.BREAKPOINTS.SMALL_MOBILE,
    isLargeMobile: screenSize.width <= CONSTANTS.BREAKPOINTS.LARGE_MOBILE,
    isShortScreen: screenSize.height <= 700,
    screenWidth: screenSize.width,
    screenHeight: screenSize.height,
  };
};

const StyledLink = ({ to, children, className = "", style = {} }) => (
  <Link
    to={to}
    className={`auth-nav-with-icon ${className}`}
    style={{
      color: CONSTANTS.COLORS.PRIMARY,
      fontWeight: "600",
      textDecoration: "none",
      transition: "all 0.3s ease",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.25rem",
      ...style,
    }}
  >
    {children}
  </Link>
);

StyledLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
};

const LoginLink = () => {
  const { isSmallMobile } = useResponsive();

  return (
    <div
      style={{
        textAlign: "center",
        marginBottom: isSmallMobile ? "0.75rem" : "1rem",
        padding: "0",
      }}
    >
      <span
        style={{
          color: "#666",
          marginRight: "0.4rem",
          fontSize: isSmallMobile ? "0.75rem" : "0.8rem",
        }}
      >
        {CONSTANTS.TEXT.LOGIN_PROMPT}
      </span>
      <StyledLink
        to={CONSTANTS.ROUTES.LOGIN}
        style={{
          fontSize: isSmallMobile ? "0.75rem" : "0.8rem",
        }}
      >
        {CONSTANTS.TEXT.LOGIN_LINK_TEXT}
      </StyledLink>
    </div>
  );
};

// Compact responsive LogoSection
const LogoSection = () => {
  const { isMobile, isSmallMobile, isShortScreen } = useResponsive();

  const logoSize = isSmallMobile
    ? "35px"
    : isMobile
    ? "40px"
    : isShortScreen
    ? "45px"
    : "50px";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isSmallMobile
          ? "0.75rem 0.5rem 0.5rem"
          : isMobile
          ? "1rem 0.75rem 0.75rem"
          : "1.25rem 1rem 1rem",
        gap: isMobile ? "0.5rem" : "0.75rem",
        flexDirection: isMobile ? "column" : "row",
        textAlign: "center",
        minHeight: "auto",
      }}
    >
      {/* Compact Logos Container */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexShrink: 0,
        }}
      >
        <Image
          src={process.env.PUBLIC_URL + CONSTANTS.IMAGES.UNIVERSITY_LOGO}
          style={{
            width: logoSize,
            height: logoSize,
            objectFit: "contain",
          }}
        />
        <Image
          src={process.env.PUBLIC_URL + CONSTANTS.IMAGES.LRC_LOGO}
          style={{
            width: logoSize,
            height: logoSize,
            objectFit: "contain",
          }}
        />
      </div>

      {/* Compact Text Content Container */}
      <div
        style={{
          flex: 1,
          textAlign: "center",
          paddingLeft: isMobile ? "0" : "0.75rem",
        }}
      >
        <Header.Subheader
          style={{
            color: "#6B8E6B",
            fontSize: isSmallMobile
              ? "0.7rem"
              : isMobile
              ? "0.75rem"
              : isShortScreen
              ? "0.8rem"
              : "0.85rem",
            marginBottom: "0.25rem",
            fontWeight: "400",
            letterSpacing: "0.3px",
            lineHeight: "1.2",
          }}
        >
          {CONSTANTS.TEXT.SYSTEM_NAME}
        </Header.Subheader>

        <Header
          style={{
            color: CONSTANTS.COLORS.PRIMARY,
            fontSize: isSmallMobile
              ? "1.3rem"
              : isMobile
              ? "1.5rem"
              : isShortScreen
              ? "1.8rem"
              : "2rem",
            fontWeight: "bold",
            lineHeight: "1.1",
            margin: "0 0 0.25rem 0",
          }}
        >
          {CONSTANTS.TEXT.WELCOME_HEADER}
        </Header>

        <Header.Subheader
          style={{
            color: "#6B8E6B",
            fontSize: isSmallMobile
              ? "0.75rem"
              : isMobile
              ? "0.8rem"
              : isShortScreen
              ? "0.85rem"
              : "0.9rem",
            fontStyle: "italic",
            fontWeight: "400",
            marginTop: "0.1rem",
          }}
        >
          {CONSTANTS.TEXT.TAGLINE}
        </Header.Subheader>
      </div>
    </div>
  );
};

// Compact footer section
const FooterSection = () => {
  const { isSmallMobile } = useResponsive();

  return (
    <div
      style={{
        textAlign: "center",
        marginTop: "0.75rem",
        padding: isSmallMobile ? "0.5rem 0.5rem 0.75rem" : "0.6rem 0 0.8rem",
      }}
    >
      <p
        style={{
          color: "#666",
          fontSize: isSmallMobile ? "0.65rem" : "0.7rem",
          lineHeight: "1.3",
          maxWidth: "550px",
          margin: "0 auto 0.5rem auto",
          padding: "0 0.5rem",
        }}
      >
        {CONSTANTS.TEXT.FOOTER_MESSAGE}
      </p>
      <StyledLink
        to={FrontSiteRoutes.home}
        className="alternative"
        style={{
          color: "#ff6b35",
          fontSize: isSmallMobile ? "0.7rem" : "0.75rem",
          gap: "0.25rem",
        }}
      >
        <Icon name="home" size="small" />
        {CONSTANTS.TEXT.HOME_LINK_TEXT}
      </StyledLink>
    </div>
  );
};

const SignupLayout = ({
  hasError,
  errorHeader,
  errorMessage,
  fetchUserProfile,
  backgroundImage,
  showLogo,
  ...props
}) => {
  const { isMobile, isSmallMobile, isShortScreen } = useResponsive();

  const containerStyle = backgroundImage
    ? {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : {};

  return (
    <Overridable
      id="Signup.layout"
      backgroundImage={backgroundImage}
      showLogo={showLogo}
      {...props}
    >
      <>
        <Notifications />
        <div className="frontsite signup-page">
          <Container
            fluid
            className="auth-page"
            style={{
              ...containerStyle,
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: isSmallMobile ? "0.5rem" : isMobile ? "1rem" : "1.5rem",
            }}
          >
            <Container
              className="login-container"
              style={{
                maxWidth: isMobile ? "100%" : isShortScreen ? "650px" : "700px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0",
              }}
            >
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.92)",
                  borderRadius: isMobile ? "12px" : "16px",
                  boxShadow: isMobile
                    ? "0 2px 15px rgba(0, 0, 0, 0.08)"
                    : "0 4px 25px rgba(0, 0, 0, 0.12)",
                  width: "100%",
                  backdropFilter: "blur(12px)",
                  overflow: "hidden",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  // Reduced height approach
                  maxHeight: isShortScreen ? "90vh" : "95vh",
                  overflowY: "auto",
                }}
              >
                {/* Compact Logo and Header Section */}
                {showLogo && <LogoSection />}

                {/* Compact Form container */}
                <div
                  style={{
                    padding: isSmallMobile
                      ? "0 1rem"
                      : isMobile
                      ? "0 1.25rem"
                      : "0 2rem",
                    paddingBottom: isSmallMobile ? "0.5rem" : "0.75rem",
                    maxWidth: isMobile ? "none" : "600px",
                    margin: "0 auto",
                  }}
                >
                  <LoginLink />
                  <div
                    style={{
                      // Ensure form doesn't get too tall
                      maxHeight: isShortScreen ? "50vh" : "none",
                      overflowY: isShortScreen ? "auto" : "visible",
                    }}
                  >
                    <SignupWithLocalAccount
                      fetchUserProfile={fetchUserProfile}
                      hasError={hasError}
                      errorHeader={errorHeader}
                      errorMessage={errorMessage}
                    />
                  </div>
                </div>

                <FooterSection />
                <Overridable id="Signup.layout.extras" />
              </div>
            </Container>
          </Container>
        </div>

        {/* Responsive CSS */}
        <style jsx>
          {`
            .signup-page {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
                sans-serif;
            }

            .login-container {
              transition: all 0.3s ease;
            }

            /* Scrollbar styling for webkit browsers */
            .login-container div::-webkit-scrollbar {
              width: 4px;
            }

            .login-container div::-webkit-scrollbar-track {
              background: rgba(0, 0, 0, 0.1);
              border-radius: 2px;
            }

            .login-container div::-webkit-scrollbar-thumb {
              background: rgba(44, 95, 45, 0.3);
              border-radius: 2px;
            }

            .login-container div::-webkit-scrollbar-thumb:hover {
              background: rgba(44, 95, 45, 0.5);
            }

            /* Desktop width constraints */
            @media (min-width: 769px) {
              .auth-page .login-container {
                max-width: 700px !important;
              }

              .login-container > div {
                max-width: 650px !important;
                margin: 0 auto !important;
              }
            }

            /* Large desktop constraints */
            @media (min-width: 1200px) {
              .auth-page .login-container {
                max-width: 650px !important;
              }

              .login-container > div {
                max-width: 600px !important;
              }
            }

            /* Mobile optimizations */
            @media (max-width: 768px) {
              .auth-page {
                padding: 0.5rem !important;
              }

              .login-container > div {
                margin: 0 !important;
                border-radius: 12px !important;
              }
            }

            /* Small mobile optimizations */
            @media (max-width: 480px) {
              .auth-page {
                padding: 0.25rem !important;
              }

              .login-container > div {
                border-radius: 8px !important;
              }
            }

            /* Short screen optimizations */
            @media (max-height: 700px) {
              .auth-page {
                align-items: flex-start !important;
                padding-top: 1rem !important;
              }
            }

            /* Very short screens */
            @media (max-height: 600px) {
              .auth-page {
                padding: 0.5rem !important;
              }
            }

            /* Landscape mobile */
            @media (max-height: 500px) and (orientation: landscape) {
              .auth-page {
                align-items: flex-start !important;
                padding: 0.25rem !important;
              }

              .login-container > div {
                max-height: 95vh !important;
                overflow-y: auto !important;
              }
            }
          `}
        </style>
      </>
    </Overridable>
  );
};

SignupLayout.propTypes = COMMON_PROP_TYPES;
SignupLayout.defaultProps = COMMON_DEFAULT_PROPS;

// Main Signup component with responsive updates
class Signup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorHeader: "",
      errorMessage: "",
    };
  }

  fetchUserProfile = () => {
    // Implementation for fetching user profile
  };

  render() {
    const { hasError, errorHeader, errorMessage } = this.state;

    return (
      <>
        <Overridable id="Signup.extras">
          <Banner />
        </Overridable>
        <SignupLayout
          hasError={hasError}
          errorHeader={errorHeader}
          errorMessage={errorMessage}
          fetchUserProfile={this.fetchUserProfile}
          {...this.props}
        />
      </>
    );
  }
}

Signup.propTypes = {
  backgroundImage: PropTypes.string,
  showLogo: PropTypes.bool,
};

Signup.defaultProps = {
  backgroundImage: null,
  showLogo: true,
};

export default Overridable.component("Signup", Signup);
